import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import toast from "react-hot-toast";

import { useApi } from "shared/lib/hooks";

import {
  useDeleteTemplate,
  useFetchEmailTemplatesList,
  useEmailGenerationInfo,
  useGenerateAIEmail,
} from "@/modules/email-templates-sidebar/queries";
import { buildGenerateEmailParams } from "@/modules/email-templates-sidebar/build-generate-email-params";
import { ComposeDraftsI } from "@/modules/email-templates-sidebar/use-compose-drafts";
import {
  AccountDetailsContextI,
  useAccountDetailsContext,
} from "@/modules/pipeline/account-details/context";
import {
  GetCampaignAttachmentsResponseI,
  glencocoClientAPI,
} from "@/api/glencoco";
import { APII } from "@/api/glencoco";
import {
  EmailTemplateI,
  GetEmailGenerationInfoResponseI,
  GetEmailGenerationStatusResponseI,
  GetEmailTemplatesListResponseI,
} from "@/api/routes/email";
import { ERRORS } from "@/constants/errors";

import { AxiosResponse } from "axios";
import {
  QueryObserverResult,
  RefetchOptions,
  UseMutateAsyncFunction,
} from "@tanstack/react-query";

import { swallow } from "@/api/legacy-swallow";

export * from "./use-email-template-local-drafts";

export const handleEmailGenerationError = (
  resp: any,
  errorMessage?: string
) => {
  /**
   * Error handling
   */
  const knownError =
    ERRORS[(resp as any)?.response?.data?.error_code as number];
  switch (resp.status) {
    case 401:
      toast.error("The prospect requested not to email");
      break;
    case 400:
      if (knownError) {
        toast.error(knownError);
      } else {
        toast.error(errorMessage || "Email Error");
      }
      break;
    default:
      toast.error(errorMessage || "Email Error");
  }
};

export interface EmailTemplatesManagerI {
  templates: AxiosResponse<GetEmailTemplatesListResponseI, any> | undefined;
  refetchTemplates: (
    options?: RefetchOptions
  ) => Promise<
    QueryObserverResult<
      AxiosResponse<GetEmailTemplatesListResponseI, any>,
      Error
    >
  >;
  activeTemplate: EmailTemplateI | undefined;
  activeTemplateId: string | undefined;
  setActiveTemplateId: (id?: string) => void;
  deleteTemplateAsync?: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    Error,
    string,
    unknown
  >;
  isEditMode: boolean | undefined;
  setEditMode: (mode: boolean) => void;
  emailGenerationConfig: GetEmailGenerationInfoResponseI | undefined;
  setEmailGenerationConfig: (config: GetEmailGenerationInfoResponseI) => void;
  campaignAttachmentsData: GetCampaignAttachmentsResponseI | null;
}

export const useEmailTemplatesManager = (): EmailTemplatesManagerI => {
  const { campaign, account, focusedContact } = useAccountDetailsContext();
  const { data: templates, refetch: refetchTemplates } =
    useFetchEmailTemplatesList();
  const { mutateAsync: deleteTemplateAsync } = useDeleteTemplate();
  const { mutateAsync: getEmailGenerationInfoAsync } = useEmailGenerationInfo();

  const [activeTemplateId, setActiveTemplateId] = useState<string>();
  const [isEditMode, setEditMode] = useState<boolean>();
  const [emailGenerationConfig, setEmailGenerationConfig] =
    useState<GetEmailGenerationInfoResponseI>();

  const activeTemplate = useMemo(() => {
    return templates?.data?.email_templates?.find(
      (t) => t.id === activeTemplateId
    );
  }, [activeTemplateId, templates]);

  // When user opens the email sidebar, get info for the email to be sent.
  // This endpoint returns an config object, which is required later for sending email.
  const createEmailGenerationConfig = useCallback(async () => {
    if (activeTemplateId && campaign?.id && account?.id && focusedContact?.id) {
      const resp = await swallow(
        getEmailGenerationInfoAsync({
          templateId: activeTemplateId,
          parameters: {
            campaign_id: campaign?.id,
            account_id: account?.id,
            contact_id: focusedContact?.id,
          },
        })
      );

      if (resp.status === 200) {
        setEmailGenerationConfig(resp.data);
        return;
      }

      /**
       * Error handling
       */
      handleEmailGenerationError(resp, "Failed to get email configurations");
    }
  }, [activeTemplateId, focusedContact, campaign, account]);

  // by default, set first template as active
  // active template is set to undefined when isEditMode is true because that implies creating a new template
  useEffect(() => {
    if (!activeTemplateId && !isEditMode) {
      setActiveTemplateId(templates?.data?.email_templates?.[0]?.id);
    }
  }, [templates?.data?.email_templates, activeTemplateId, isEditMode]);

  useEffect(() => {
    createEmailGenerationConfig();
  }, [activeTemplateId]);

  const campaignAttachmentsFetcher = useMemo(
    () =>
      campaign?.id
        ? (api: APII) => swallow(api.getCampaignAttachments(campaign.id))
        : null,
    [campaign]
  );

  const [{ data: campaignAttachmentsData }] = useApi({
    apiFactory: glencocoClientAPI,
    fetcher: campaignAttachmentsFetcher,
    onError: () => toast.error("Error getting available attachments."),
  });

  return {
    templates,
    refetchTemplates,
    activeTemplate,
    activeTemplateId,
    setActiveTemplateId,
    deleteTemplateAsync,
    isEditMode,
    setEditMode,
    emailGenerationConfig,
    setEmailGenerationConfig,
    campaignAttachmentsData,
  };
};

export interface AccountEmailAIGenerationOptionalPropsI {
  subject?: string;
  body?: string;

  /**
   * NOTE
   * If the email is part of a sequence, we need to pass the sequence order
   */
  sequenceOrder?: number;
}

export interface EmailAIGenerationI {
  isAIGenerationComplete: boolean;
  isAIGenerationStarted: boolean;
  isEmailSent: boolean;
  emailData: GetEmailGenerationStatusResponseI | undefined;
  setEmailData: Dispatch<
    SetStateAction<GetEmailGenerationStatusResponseI | undefined>
  >;
  setIsEmailSent: (isSent: boolean) => void;
  setIsAIGenerationComplete: (isComplete: boolean) => void;
  setIsAIGenerationStarted: (isStarted: boolean) => void;
  handleComposeWithAI: () => Promise<boolean | undefined>;
}

export const useAccountEmailAIGeneration = (
  template?: EmailTemplateI,
  optional?: AccountEmailAIGenerationOptionalPropsI,
  composeDrafts?: ComposeDraftsI
): EmailAIGenerationI => {
  const accountDetails = useAccountDetailsContext();
  const { campaign, account, focusedContact } = accountDetails;

  const accountDetailsRef = useRef<AccountDetailsContextI>(accountDetails);
  useEffect(() => {
    accountDetailsRef.current = accountDetails;
  }, [accountDetails]);

  const { mutateAsync: getEmailGenerationInfoAsync } = useEmailGenerationInfo();
  const { mutateAsync: generateAIEmailAsync } = useGenerateAIEmail();

  const [isAIGenerationComplete, setIsAIGenerationComplete] = useState(false);
  const [isAIGenerationStarted, setIsAIGenerationStarted] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);

  const [emailData, setEmailData] = useState<
    GetEmailGenerationStatusResponseI | undefined
  >(
    optional
      ? {
          subject: optional?.subject,
          body: optional?.body,
        }
      : undefined
  );

  const handleComposeWithAI = async () => {
    if (isAIGenerationStarted) {
      // still on-going AI generation
      return;
    }

    setEmailData({ ...(emailData || {}), subject: "", body: "" });

    setIsAIGenerationStarted(true);
    setIsAIGenerationComplete(false);

    let success = false;

    /**
     * try/finally, so the two flags always settle whatever happens. The
     * generation call is a raw fetch and throws on a non-2xx or a timeout; an
     * escape past the flag reset leaves the button spinning until the rep
     * reloads the page.
     */
    try {
      /**
       * Template id is not needed for creating new templates
       */
      if (campaign?.id && account?.id && focusedContact?.id) {
        const resp = await swallow(
          getEmailGenerationInfoAsync({
            templateId: template?.id as string,
            parameters: {
              campaign_id: campaign?.id,
              account_id: account?.id,
              contact_id: focusedContact?.id,
            },
          })
        );

        if (resp.status === 200) {
          /**
           * The prompt and the transcript come from what is on screen, not
           * from what the server last persisted. Both save asynchronously, and
           * generating inside either window produced an email written from the
           * previous text - or, for a transcript never saved at all, from no
           * call whatsoever.
           */
          const parameters = buildGenerateEmailParams({
            info: resp.data,
            promptDraft: composeDrafts?.getPromptDraft(template?.id),
            callContext: composeDrafts?.getCallContextDraft(focusedContact.id),
            sequenceOrder: optional?.sequenceOrder,
          });

          const generated = await generateAIEmailAsync({ parameters });

          setEmailData((prev) => ({
            ...prev,
            subject: generated.subject,
            body: generated.body_html,
          }));

          success = true;
        } else {
          /**
           * `swallow` hands a failed request back as a value, so a non-200
           * lands here instead of in the catch below: this branch keeps the
           * specific 401/400 messages that handleEmailGenerationError knows,
           * which the generic catch would flatten into "Failed to generate".
           */
          handleEmailGenerationError(
            resp,
            "Failed to get email configurations"
          );
        }
      }
    } catch (error) {
      /**
       * fetchWithTimeout aborts the request at AI_EMAIL_GENERATION_TIMEOUT_MS,
       * and an aborted fetch rejects with a DOMException whose message is
       * browser internals ("The user aborted a request."). Showing that to a
       * rep explains nothing, so the timeout gets named.
       */
      if ((error as Error)?.name === "AbortError") {
        toast.error("Email generation timed out. Please try again.");
      } else {
        toast.error(
          (error as Error)?.message ||
            "Failed to generate the email. Please try again."
        );
      }
    } finally {
      setIsAIGenerationComplete(true);
      setIsAIGenerationStarted(false);
    }

    return success;
  };

  useEffect(() => {
    // reset/initialize content for selected template

    setIsEmailSent(false);
    setIsAIGenerationComplete(false);
    setIsAIGenerationStarted(false);
  }, [template?.id]);

  return {
    isAIGenerationComplete,
    isAIGenerationStarted,
    isEmailSent,
    emailData,
    setEmailData,
    setIsEmailSent,
    setIsAIGenerationComplete,
    setIsAIGenerationStarted,
    handleComposeWithAI,
  };
};
